/**
 * Fuzzy matching of a **Backlog user display name** to a Migratus
 * **assignee** display name.
 *
 * This is the deliberate-but-imperfect heuristic the Settings page
 * "Backlog ユーザーを自動マッチ" button proposes from, and the one the
 * one-off backfill script used. It lives in `@mct/shared` so the web UI
 * (proposal) and Functions (backfill / scripts) share ONE definition —
 * the explicit `backlogUserId` map persisted via the picker is always the
 * authoritative resolver; this only seeds first-time suggestions.
 *
 * Two strategies, in order:
 *   1. whitespace-stripped exact match (handles 「西野　貴士」⇔「西野貴士」)
 *   2. surname-prefix match — the Backlog full name *starts with* the
 *      (shorter) Migratus name, which is usually a surname
 *      (handles 「佐野友貴子」⇔「佐野」, 「岩﨑　武弘」⇔「岩崎」 after NFKC).
 *
 * Names are NFKC-normalized first so half/full-width and kanji
 * compatibility variants (﨑 → 崎) collapse before comparison.
 */

/** A match-able assignee: a stable id plus the display name to compare. */
export interface AssigneeNameCandidate {
  id: string;
  name: string;
}

export type AssigneeNameMatchType = 'exact' | 'prefix' | 'prefix-ambiguous';

export interface AssigneeNameMatch {
  /** The winning assignee's id. */
  id: string;
  matchType: AssigneeNameMatchType;
  /** How many assignees matched (1 for `exact` / `prefix`; >1 for ambiguous). */
  candidateCount: number;
}

/**
 * JIS-X-0213 / CJK-compatibility kanji variants the personnel data mixes
 * with their standard glyphs. NFKC does NOT fold these (they carry no
 * Unicode decomposition), so we map them explicitly. Mirrors
 * `functions/src/core/backfillAssignees.ts`'s `KANJI_VARIANT_MAP` and
 * extends it with the other surnames seen in practice.
 */
const KANJI_VARIANT_MAP: Readonly<Record<string, string>> = {
  '﨑': '崎', // U+FA11 → U+5D0E  (岩﨑 → 岩崎)
  '髙': '高', // U+9AD9 → U+9AD8  (髙橋 → 高橋)
  '德': '徳', // U+5FB3 → U+5FB3? common 德/徳 swap
  '濵': '濱', // U+6FF5 variant
  '塚': '塚',
};

function canonicalizeKanji(s: string): string {
  let out = '';
  for (const ch of s) out += KANJI_VARIANT_MAP[ch] ?? ch;
  return out;
}

/**
 * Collapse a name to its comparison key: NFKC-normalize (folds full/half
 * width and turns the ideographic space U+3000 into a regular space),
 * canonicalize kanji variants (﨑→崎), then strip ALL whitespace. Returns
 * `''` for nullish / blank input.
 */
export function stripAssigneeName(s: string | null | undefined): string {
  if (!s) return '';
  return canonicalizeKanji(s.normalize('NFKC')).replace(/[\s　]/g, '');
}

/**
 * Resolve one Backlog display name against the project's assignee roster.
 * Returns the best match or `null` when nothing plausible lines up.
 *
 * Prefix matches require the Migratus name to be ≥ 2 chars (after
 * stripping) to avoid 1-char false positives. When several assignees
 * prefix-match, the LONGEST (most specific) wins and the result is tagged
 * `prefix-ambiguous` so the UI can flag it for human review.
 */
export function matchBacklogNameToAssignee(
  backlogName: string | null | undefined,
  assignees: readonly AssigneeNameCandidate[],
): AssigneeNameMatch | null {
  const bn = stripAssigneeName(backlogName);
  if (!bn) return null;

  // 1. whitespace-stripped exact match (first wins, deterministic).
  for (const a of assignees) {
    if (stripAssigneeName(a.name) === bn) {
      return { id: a.id, matchType: 'exact', candidateCount: 1 };
    }
  }

  // 2. surname-prefix match.
  const candidates: { id: string; stripped: string }[] = [];
  for (const a of assignees) {
    const an = stripAssigneeName(a.name);
    if (an.length < 2) continue;
    if (bn.startsWith(an)) candidates.push({ id: a.id, stripped: an });
  }
  if (candidates.length === 0) return null;
  if (candidates.length === 1) {
    return { id: candidates[0]!.id, matchType: 'prefix', candidateCount: 1 };
  }
  // Multiple — prefer the longest (most specific) stripped match.
  candidates.sort((a, b) => b.stripped.length - a.stripped.length);
  return {
    id: candidates[0]!.id,
    matchType: 'prefix-ambiguous',
    candidateCount: candidates.length,
  };
}
